import {
  IFamilyMembers,
  IRecordMedicalInheritedFamily,
  MedicalInheritedFamilyDiseaseState,
  RecordMedicalInheritedFamilyAction
} from '../../models/record-medical-inherited-family';
import {DiseasesState, IDisease, IDiseaseCategory} from '../../models/diseases';
import {cloneDeep} from 'lodash';
import {IRecordEmit} from '../../interfaces/record';
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {GuidService} from '../../services/guid/guid.service';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {Store} from '@ngxs/store';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {StorageService} from '../../services/storage/storage.service';
import {Loader} from '../../models/loader/loader.redux';
import {ActivatedRoute, Params} from '@angular/router';
import {IUser} from '../../models/auth';
import {ServiceAction, ServiceState} from '../../models/service';
import {Logger} from '../../class/helper/logger.class';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-familial-medical',
  templateUrl: './record-history-familial-medical.component.html',
  styleUrls: ['./record-history-familial-medical.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordHistoryFamilialMedicalComponent implements OnInit, OnDestroy, OnChanges {
  // tslint:disable:variable-name
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(MedicalInheritedFamilyDiseaseState.getWasDeployed);
  familyMembersOptions: IFamilyMembers[] = [];

  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;
  limit = 100;
  userLogged: IUser;
  @Input() isInnerRecord: boolean;
  @Input() recordMedicalHerded: IRecordMedicalInheritedFamily;
  @Output() recordMedicalHerdedChange: EventEmitter<IRecordEmit> = new EventEmitter<IRecordEmit>();

  wasDeployedSubscription: Subscription;

  diseaseCategories: IDiseaseCategory[] = [];
  familyMembers: IFamilyMembers[] = [];

  loadInfo: boolean;
  skeletonTemplate: any[] = [];
  disableForm = false;
  constructor(
    public utilitiesService: UtilitiesService,
    private guidService: GuidService,
    private store: Store,
    private messageAlert: MessageAlertService,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private skeletonService: SkeletonService,
  ) {
    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordMedicalHerded && !this.isInnerRecord) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.recordMedicalHerded) {
      this.initValuesHttp().then();
    }
  }

  /**
   * Inicializa las variables necesarias.
   */
  ngOnInit(): void {
    this.familyMembersOptions = [
      {
        relation: 'Padre/Madre',
        quantity: 0,
        max: 2
      },
      {
        relation: 'Hermanos(as)',
        quantity: 0
      },
      {
        relation: 'Tíos(as)',
        quantity: 0
      },
      {
        relation: 'Abuelos(as)',
        quantity: 0,
        max: 4
      }];
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS);
  }

  /**
   * Hace las peticiones iniciales necesarias al servidor.
   */
  async initValuesHttp(): Promise<void> {
    try {
      // await firstValueFrom((this.store.dispatch(new Loader(true, 'Cargando historial médico heredo-familiar ...'))));
      this.getParams();
      this.userLogged = this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user;
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom((this.store.selectOnce(ServiceState.getServiceSelectedId)));
      if (!this.serviceId) {
        await firstValueFrom((this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId))));
        this.serviceId = await firstValueFrom((this.store.selectOnce(ServiceState.getServiceSelectedId)));
      }
      if (!(this.recordMedicalHerded && this.isInnerRecord)) {
        await firstValueFrom((this.store.dispatch(new RecordMedicalInheritedFamilyAction.GetActive(this.serviceId, this.patientId))));
        this.recordMedicalHerded = cloneDeep(await firstValueFrom((this.store.selectOnce(MedicalInheritedFamilyDiseaseState.getRecordMedicalInheritedFamily))));
      }
      const diseaseCategories = cloneDeep(await firstValueFrom(this.store.selectOnce(DiseasesState.getDiseaseCategories)));
      diseaseCategories.forEach((category: IDiseaseCategory) => {
        this.diseaseCategories.push({
          ...category,
          diseases: category.diseases ? category.diseases.map((_disease) => {
            const existsDisease = this.recordMedicalHerded.diseases.find(item => item.diseaseId === _disease._id);
            return {
              ..._disease,
              active: !!existsDisease,
              familyMembers: existsDisease ? existsDisease.familyMembers : [],
              options: existsDisease ? this.arrayUnion(
                cloneDeep(this.familyMembersOptions),
                existsDisease.familyMembers || []
              ) : cloneDeep(this.familyMembersOptions)
            };
          }) : category.diseases ? category.diseases.map((_disease) => {
            return {
              ..._disease,
              active: false,
              familyMembers: [],
              options: cloneDeep(this.familyMembersOptions)
            };
          }) : []
        });
      });
      // Bloquear campos para usuarios pacientes
      this.disableForm = !this.userLogged || !!this.userLogged?.patients;
      setTimeout(() => {
        this.loadInfo = true;
      });
      this.recordMedicalHerdedChange.emit({ record: this.recordMedicalHerded, updated: false });
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  arrayUnion(arr1, arr2): any[] {
    for (const item of arr1) {
      item.quantity = (arr2.find(element => element.relation === item.relation))?.quantity || 0;
      if (item.relation === 'Padre/Madre') {
        item.max = 2;
      } else if (item.relation === 'Abuelos(as)') {
        item.max = 4;
      }
    }
    return cloneDeep(arr1);
  }

  /**
   * Obtiene los parametros de la url
   */
  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  /**
   * Check Disease
   * @param value
   * @param categoryIndex
   * @param diseaseIndex
   */
  checkedDiseaseChange(value: boolean, categoryIndex: number, diseaseIndex): void {
    const category = this.diseaseCategories[categoryIndex];
    if (!category) return;

    const disease = category.diseases[diseaseIndex];
    if (!disease) return;

    if (value) {
      // se debe agregar a los datos de la ficha a guardar
      this.pushDisease(disease, category.name);
    } else {
      // se debe remover de los datos de la ficha a guardar
      this.removeDisease(disease);
    }
  }

  /**
   *
   * @param disease
   */
  removeDisease(disease: IDisease): void {
    const existsIndex = this.recordMedicalHerded.diseases.findIndex(_disease => _disease.diseaseId === disease._id);
    if (existsIndex === -1) return;
    this.recordMedicalHerded.diseases.splice(existsIndex, 1);
    this.setDiseaseAsRequired(disease.diseaseCategoryId, disease._id, false);
  }

  /**
   *
   * @param disease
   * @param categoryName
   */
  pushDisease(disease: IDisease, categoryName: string): void {
    const exists = this.recordMedicalHerded.diseases.find(_disease => _disease.diseaseId === disease._id);
    if (exists) return;

    const item = cloneDeep(disease);

    this.recordMedicalHerded.diseases.push({
      diseaseCategoryId: item.diseaseCategoryId,
      diseaseCategoryName: categoryName,
      diseaseId: item._id,
      diseaseIdentifier: item.identifier,
      diseaseName: item.name,
      familyMembers: item.familyMembers || item.options || []
    });
  }

  /**
   *
   * @param $event
   * @param disease IDisease
   * @param data
   */
  changeQuantity($event, disease, data): void {
    if (typeof $event === 'string') return;

    const existsIndex = this.recordMedicalHerded.diseases.findIndex(_disease => _disease.diseaseId === disease._id);
    if (existsIndex === -1) return;

    const existsInArray = this.recordMedicalHerded.diseases[existsIndex].familyMembers.findIndex(family => family.relation === data.relation);
    if (existsInArray !== -1) {
      this.recordMedicalHerded.diseases[existsIndex].familyMembers[existsInArray] = cloneDeep(data);
    } else {
      this.recordMedicalHerded.diseases[existsIndex].familyMembers.push(cloneDeep(data));
    }

    // TODO: ---
    this.setDiseaseAsRequired(disease.diseaseCategoryId, disease._id, false);
  }

  checkFields(): boolean {
    const errorDiseases = this.checkDiseasesRelatives();
    if (errorDiseases.length > 0) {
      this.messageAlert.createMessage('warning', `Ingresa al menos un pariente biológico para ${errorDiseases.length > 1 ? 'las enfermedades' : 'la enfermedad'} ${errorDiseases.join(', ')}.`);
      return false;
    }
    return true;
  }

  async onSave(): Promise<void> {
    try {

      if (!this.checkFields()) return;

      await firstValueFrom((this.store.dispatch(new Loader(true, 'Guardando las enfermedades ...'))));
      const { diseases } = cloneDeep(this.recordMedicalHerded);
      await firstValueFrom(this.store.dispatch(new RecordMedicalInheritedFamilyAction.PatchActive(
        this.serviceId,
        this.patientId,
        { diseases }
      )));
      this.messageAlert.createMessage('success', 'Historial médico heredo-familiar guardado exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      Logger.log('Ocurrio un error', e);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * check that the diseases have at least one relative
   * @private
   */
  private checkDiseasesRelatives(): string[] {
    const errorDiseases: string[] = [];

    for (const disease of this.recordMedicalHerded.diseases) {
      const familyMembersCount = disease.familyMembers.reduce((acc: number, {quantity}: IFamilyMembers) => acc + quantity, 0);
      if (familyMembersCount !== 0) continue;

      errorDiseases.push(disease.diseaseName);
      this.setDiseaseAsRequired(disease.diseaseCategoryId, disease.diseaseId, true);
    }

    return errorDiseases;
  }

  emitChangeUpdate(): void {
    this.recordMedicalHerded.updatedAt = new Date();
    this.recordMedicalHerdedChange.emit({ record: this.recordMedicalHerded, updated: true });
  }

  /**
   *
   * @param categoryId
   * @param diseaseId
   * @param required
   * @private
   */
  private setDiseaseAsRequired(categoryId: string, diseaseId: string, required = false): void {
    const categoryIndex = this.diseaseCategories.findIndex(({_id}: IDiseaseCategory) => _id?.toString() === categoryId.toString());
    if (categoryIndex === -1) return;

    const diseaseIndex = this.diseaseCategories[categoryIndex].diseases?.findIndex(({_id}: IDisease) => _id?.toString() === diseaseId.toString());
    if (diseaseIndex === -1) return;

    this.diseaseCategories[categoryIndex].diseases[diseaseIndex].required = required;
  }

  async ngOnDestroy(): Promise<void> {
    await firstValueFrom((this.store.dispatch(new RecordMedicalInheritedFamilyAction.Reset())));
    this.wasDeployedSubscription.unsubscribe();
  }
}
